import { Container, Input, Mediator } from '@studio/design-system/input';
import type { Size } from '@studio/design-system/input';
import { toString } from '@tryiris-ai/js-types';
import type { T_Maybe } from '@tryiris-ai/js-types';
import type { ReactElement } from 'react';
import { useCallback, useId, useState } from 'react';
import { twMerge } from 'cn';
import { Column, Exhibit } from './styled.jsx';

type State = [T_Maybe<string>, string, string, string];

const Specimen = ({ size }: { size: Size }): ReactElement => {
  const [state, setState] = useState<State>([
    undefined,
    'some value goes here',
    'abcdef',
    'cool beans'
  ]);

  const [isEditing, setEditing] = useState(false);

  const labelId = useId();

  const handleChange = useCallback(
    (position: number) => (nextValue: string) => {
      setState(
        (µ) =>
          µ.map((currentValue, index) => (position === index ? nextValue : currentValue)) as State
      );
    },
    []
  );

  return (
    <>
      <Input
        focusOnMount
        onChange={handleChange(0)}
        placeholder='Placeholder text'
        size={size}
        value={state[0]}
      />

      <Container htmlFor={labelId} label='Some value'>
        <Input id={labelId} onChange={handleChange(1)} size={size} value={state[1]} />
      </Container>

      <Container label='Password'>
        <Input onChange={handleChange(2)} size={size} type='password' value={state[2]} />
      </Container>

      <Container label='Readonly Input'>
        <Input readOnly size={size} value={state[2]} />
      </Container>

      <Container label='Disabled Input'>
        <Input disabled size={size} value={state[2]} />
      </Container>

      {['sm', 'md'].includes(size) && (
        <Container label='Double-Click To Edit'>
          {isEditing ? (
            <Mediator
              focusOnMount
              onCancel={(): void => {
                setEditing(false);
              }}
              onCommit={(value: string): void => {
                const nextValue = toString(value);

                if (nextValue) {
                  handleChange(3)(nextValue);
                }

                setEditing(false);
              }}
              selectOnMount
              size={size}
              value={state[3]}
            />
          ) : (
            <div
              className={twMerge(
                'flex items-center border border-secondary/50 px-10 text-foreground',
                size === 'sm' ? 'h-28 text-sm' : 'h-34 text-md'
              )}
              onDoubleClick={(): void => {
                setEditing((b) => !b);
              }}
            >
              {state[3]}
            </div>
          )}
        </Container>
      )}
    </>
  );
};

/** `<Inputs>` exhibit. */
export const Inputs = (): ReactElement => (
  <Exhibit title='Inputs'>
    <div className='flex gap-40'>
      {(['sm', 'md', 'lg', 'xl'] as const).map((size) => (
        <Column key={size}>
          <div className='font-bold'>{size.toUpperCase()}</div>
          <Specimen size={size} />
        </Column>
      ))}
    </div>
  </Exhibit>
);
